import {
	Attachment,
	AttachmentSource,
	AttachableDocument,
	EmailTemplate,
	MultiSelectOption,
} from "@dune/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
	useSaveDraftEmail,
	useSendEmail,
	useUpdateDraftEmail,
} from "../api/mutations/mail.mutations";
import {
	getFileUrlByBucketAndKey,
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../api/queries/files.queries";
import { useMe } from "../api/queries/users.queries";
import { useDebouncedUserSearch } from "./useDebouncedUserSearch";
import { useEmailTemplatesForEntity } from "./useEmailTemplates";
import { useComposerShouldClose, useEmailActions } from "../stores/email.store";
import { addSignatureToContent } from "../utils/signature.utils";
import type { RelatedEntityType } from "@dune/types";
import { useDocumentToFile, type DocumentFileInfo } from "./useDocumentToFile";

export interface EmailFormValues {
	to: MultiSelectOption[];
	cc: MultiSelectOption[];
	bcc: MultiSelectOption[];
	subject: string;
	content: string;
}

export interface InitialDataComplete {
	to: MultiSelectOption[];
	cc: MultiSelectOption[];
	bcc: MultiSelectOption[];
	subject: string;
	content: string;
	attachments: Attachment[];
}

export type LocalAttachment = Attachment & {
	url?: string;
	contentType?: string;
	isUploading: boolean;
	error?: boolean;
	source: AttachmentSource;
	entityDocumentId?: string; // Generic field for entity-specific document IDs (brokerDocumentId, propositionDocumentId, etc.)
};

export interface UseEmailComposerProps {
	initialData?: Partial<InitialDataComplete>;
	draftId?: string;
	entityId: number;
	entityType: RelatedEntityType;
	replyTo?: string;
	onClose?: () => void;
	mapAttachmentToPayload?: (
		att: LocalAttachment & { url: string; contentType: string },
	) => Record<string, unknown>;
}

export interface UseEmailComposerReturn {
	// Form
	control: ReturnType<typeof useForm<EmailFormValues>>["control"];
	handleSubmit: ReturnType<typeof useForm<EmailFormValues>>["handleSubmit"];
	watch: ReturnType<typeof useForm<EmailFormValues>>["watch"];
	reset: ReturnType<typeof useForm<EmailFormValues>>["reset"];
	isDirty: boolean;

	// State
	attachments: LocalAttachment[];
	setAttachments: React.Dispatch<React.SetStateAction<LocalAttachment[]>>;
	showSaveDraftPrompt: boolean;
	setShowSaveDraftPrompt: React.Dispatch<React.SetStateAction<boolean>>;
	showCcBcc: boolean;
	setShowCcBcc: React.Dispatch<React.SetStateAction<boolean>>;
	showTemplateDialog: boolean;
	setShowTemplateDialog: React.Dispatch<React.SetStateAction<boolean>>;
	isSignatureDialogOpen: boolean;
	setIsSignatureDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

	// Search
	toSearch: ReturnType<typeof useDebouncedUserSearch>;
	ccSearch: ReturnType<typeof useDebouncedUserSearch>;
	bccSearch: ReturnType<typeof useDebouncedUserSearch>;

	// Templates
	templates: EmailTemplate[];
	isLoadingTemplates: boolean;
	handleTemplateSelection: (templateId: string) => Promise<void>;

	// Handlers
	handleAttachmentAdd: (files: File[]) => Promise<void>;
	handleAttachmentRemove: (id: string) => void;
	handleExistingDocumentsAdd: <T>(params: {
		documents: AttachableDocument[];
		originalDocuments: T[] | undefined;
		findOriginal: (documents: T[], id: string) => T | undefined;
		extractInfo: (doc: T) => DocumentFileInfo;
		extractId: (doc: T) => string;
		entityName: string;
	}) => Promise<void>;
	handleSendEmail: () => Promise<void>;
	handleSaveDraft: () => Promise<void>;
	handleDiscardDraft: () => void;
	getHandleEmailSearch: (
		field: "to" | "cc" | "bcc",
	) => (q: string) => Promise<MultiSelectOption[]>;

	// Mutations
	sendEmailMutation: ReturnType<typeof useSendEmail>;
}

export const useEmailComposer = ({
	initialData,
	draftId,
	entityId,
	entityType,
	replyTo,
	onClose,
	mapAttachmentToPayload,
}: UseEmailComposerProps): UseEmailComposerReturn => {
	const { setComposerDirty, setComposerShouldClose, clearComposerState } =
		useEmailActions();
	const composerShouldClose = useComposerShouldClose();
	const { me } = useMe();

	const {
		templates,
		isLoading: isLoadingTemplates,
		loadTemplateWithData,
	} = useEmailTemplatesForEntity({
		entityType,
		entityId: entityId.toString(),
	});

	const mergedInitialData: InitialDataComplete = useMemo(() => {
		const baseData = {
			to: initialData?.to ?? [],
			cc: initialData?.cc ?? [],
			bcc: initialData?.bcc ?? [],
			subject: initialData?.subject ?? "",
			content: initialData?.content ?? "",
			attachments: initialData?.attachments ?? [],
		};

		if (draftId) {
			return baseData;
		}

		const hasExistingSignature =
			baseData.content.includes("border-top: 1px solid #e5e7eb") ||
			(me?.emailSignature && baseData.content.includes(me.emailSignature));

		const contentWithSignature = hasExistingSignature
			? baseData.content
			: addSignatureToContent(
					baseData.content,
					me?.emailSignature || undefined,
				);

		return {
			...baseData,
			content: contentWithSignature,
		};
	}, [initialData, me?.emailSignature, draftId]);

	const [attachments, setAttachments] = useState<LocalAttachment[]>(
		mergedInitialData.attachments.map((att) => ({
			...att,
			isUploading: false,
			source: "EXISTING",
		})),
	);
	const [showSaveDraftPrompt, setShowSaveDraftPrompt] = useState(false);
	const [showCcBcc, setShowCcBcc] = useState(false);
	const [showTemplateDialog, setShowTemplateDialog] = useState(false);
	const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);

	const toSearch = useDebouncedUserSearch();
	const ccSearch = useDebouncedUserSearch();
	const bccSearch = useDebouncedUserSearch();

	const { mutateAsync: saveDraftAsync } = useSaveDraftEmail({
		entityId: entityId,
		entityType: entityType,
	});
	const sendEmailMutation = useSendEmail({
		entityId: entityId,
		entityType: entityType,
	});
	const { mutateAsync: updateDraftAsync } = useUpdateDraftEmail({
		entityId: entityId,
		entityType: entityType,
	});

	const handleTemplateSelection = async (templateId: string) => {
		if (!templateId || templateId === "none") {
			return;
		}

		try {
			const templateData = await loadTemplateWithData(templateId);

			const formValues = watch();
			const contentWithSignature = addSignatureToContent(
				templateData.htmlContent,
				me?.emailSignature || undefined,
			);

			reset({
				...formValues,
				subject: templateData.subject,
				content: contentWithSignature,
			});

			setComposerDirty(true);
			toast.success(
				`Template "${templateData.template.name}" chargé avec succès`,
			);
		} catch (error) {
			console.error("Error loading template:", error);
			toast.error("Erreur lors du chargement du template");
		}
	};

	const {
		control,
		handleSubmit,
		formState: { isDirty },
		watch,
		reset,
	} = useForm<EmailFormValues>({
		defaultValues: {
			to: mergedInitialData.to,
			cc: mergedInitialData.cc,
			bcc: mergedInitialData.bcc,
			subject: mergedInitialData.subject,
			content: mergedInitialData.content,
		},
	});

	useEffect(() => {
		setComposerDirty(isDirty);
	}, [isDirty, setComposerDirty]);

	useEffect(() => {
		reset({
			to: mergedInitialData.to,
			cc: mergedInitialData.cc,
			bcc: mergedInitialData.bcc,
			subject: mergedInitialData.subject,
			content: mergedInitialData.content,
		});
		setAttachments(
			mergedInitialData.attachments.map((att) => ({
				...att,
				isUploading: false,
				source: "EXISTING",
			})),
		);
		setComposerDirty(false);
	}, [reset, setComposerDirty, mergedInitialData]);

	useEffect(() => {
		if (composerShouldClose) {
			setComposerShouldClose(false);

			if (isDirty) {
				setShowSaveDraftPrompt(true);
			} else if (onClose) {
				onClose();
			}
		}
	}, [composerShouldClose, isDirty, onClose, setComposerShouldClose]);

	const handleAttachmentAdd = async (files: File[]) => {
		setAttachments((prev) => {
			const existingFileKeys = new Set(
				prev.map((att) => `${att.name}-${att.size}`),
			);
			const uniqueFiles = files.filter(
				(file) => !existingFileKeys.has(`${file.name}-${file.size}`),
			);

			if (uniqueFiles.length === 0) {
				return prev;
			}

			const newEntries: LocalAttachment[] = uniqueFiles.map((file) => ({
				id: uuidv4(),
				file,
				name: file.name,
				size: file.size,
				type: file.type,
				isUploading: true,
				source: "NEW",
			}));

			const uploadPromises = newEntries.map(async (entry) => {
				const file = entry.file;
				try {
					const presign = await getSignedPutUrl({
						bucket: "public",
						contentType: file.type,
					});
					await uploadFileToS3(presign.signedUrl, file);
					await registerS3File({
						bucket: "public",
						key: presign.key,
						name: file.name,
						mimeType: file.type,
					});
					const url = await getFileUrlByBucketAndKey("public", presign.key);
					return {
						id: entry.id,
						url,
						contentType: file.type,
						error: false,
					};
				} catch (_error) {
					return {
						id: entry.id,
						url: undefined,
						contentType: undefined,
						error: true,
					};
				}
			});

			Promise.allSettled(uploadPromises).then((results) => {
				const uploadedEntryIds = new Set(newEntries.map((e) => e.id));
				setAttachments((prevAtts) =>
					prevAtts.map((att) => {
						if (!uploadedEntryIds.has(att.id) || !att.isUploading) {
							return att;
						}
						const result = results.find(
							(r) => r.status === "fulfilled" && r.value.id === att.id,
						);
						if (!result || result.status !== "fulfilled") {
							return { ...att, isUploading: false, error: true };
						}
						const { url, contentType, error } = result.value;
						return {
							...att,
							url,
							contentType,
							isUploading: false,
							error,
							source: "NEW",
						};
					}),
				);
				setComposerDirty(true);
			});

			return [...prev, ...newEntries];
		});
	};

	const handleAttachmentRemove = (id: string) => {
		setAttachments(attachments.filter((attachment) => attachment.id !== id));
		setComposerDirty(true);
	};

	const { convertDocumentsToFiles } = useDocumentToFile();

	const handleExistingDocumentsAdd = useCallback(
		async <T>({
			documents,
			originalDocuments,
			findOriginal,
			extractInfo,
			extractId,
			entityName,
		}: {
			documents: AttachableDocument[];
			originalDocuments: T[] | undefined;
			findOriginal: (documents: T[], id: string) => T | undefined;
			extractInfo: (doc: T) => DocumentFileInfo;
			extractId: (doc: T) => string;
			entityName: string;
		}) => {
			try {
				if (!originalDocuments) {
					throw new Error(`No ${entityName} documents available`);
				}

				const mappedDocuments = documents.map((attachableDoc): T => {
					const originalDoc = findOriginal(originalDocuments, attachableDoc.id);
					if (!originalDoc) {
						throw new Error(
							`Original ${entityName} document not found for ID: ${attachableDoc.id}`,
						);
					}
					return originalDoc;
				});

				const files = await convertDocumentsToFiles(
					mappedDocuments,
					extractInfo,
				);

				const newEntries: LocalAttachment[] = files.map((file, index) => ({
					id: uuidv4(),
					file,
					name: file.name,
					size: file.size,
					type: file.type,
					isUploading: false,
					source: "EXISTING",
					entityDocumentId: extractId(mappedDocuments[index]),
				}));

				const uploadPromises = newEntries.map(async (entry) => {
					const file = entry.file;
					try {
						const presign = await getSignedPutUrl({
							bucket: "public",
							contentType: file.type,
						});
						await uploadFileToS3(presign.signedUrl, file);
						await registerS3File({
							bucket: "public",
							key: presign.key,
							name: file.name,
							mimeType: file.type,
						});
						const url = await getFileUrlByBucketAndKey("public", presign.key);
						return {
							id: entry.id,
							url,
							contentType: file.type,
							error: false,
						};
					} catch (error) {
						console.error(
							"Erreur lors de l'upload du document existant:",
							error,
						);
						return {
							id: entry.id,
							url: undefined,
							contentType: undefined,
							error: true,
						};
					}
				});

				const results = await Promise.allSettled(uploadPromises);

				const updatedEntries = newEntries.map((entry) => {
					const result = results.find(
						(r) => r.status === "fulfilled" && r.value.id === entry.id,
					);
					if (!result || result.status !== "fulfilled") {
						return { ...entry, error: true };
					}
					const { url, contentType, error } = result.value;
					return {
						...entry,
						url,
						contentType,
						error,
					};
				});

				setAttachments((prev) => [...prev, ...updatedEntries]);
				setComposerDirty(true);
			} catch (error) {
				console.error("Erreur lors de l'ajout des documents existants:", error);
				toast.error("Erreur lors de l'ajout des documents");
			}
		},
		[convertDocumentsToFiles, setComposerDirty],
	);

	const formatRecipientsForApi = (
		recipients: MultiSelectOption[],
	): string[] => {
		return recipients.map((recipient) => recipient.value);
	};

	const handleSendEmail = handleSubmit(async (formData) => {
		const attachmentsPayload = attachments
			.filter(
				(att): att is LocalAttachment & { url: string; contentType: string } =>
					!att.isUploading &&
					att.url !== undefined &&
					att.contentType !== undefined,
			)
			.map((att) => {
				const basePayload = {
					fileName: att.name,
					contentType: att.contentType,
					size: att.size,
					url: att.url,
					source: att.source,
				};
				return mapAttachmentToPayload
					? { ...basePayload, ...mapAttachmentToPayload(att) }
					: basePayload;
			});

		const apiData = {
			to: formatRecipientsForApi(formData.to),
			cc:
				formData.cc.length > 0
					? formatRecipientsForApi(formData.cc)
					: undefined,
			bcc:
				formData.bcc.length > 0
					? formatRecipientsForApi(formData.bcc)
					: undefined,
			subject: formData.subject,
			html: formData.content,
			attachments:
				attachmentsPayload.length > 0 ? attachmentsPayload : undefined,
			entityType: entityType,
			entityId: Number(entityId),
			draftId: draftId ? Number(draftId) : undefined,
			replyTo: replyTo,
		};

		try {
			await sendEmailMutation.mutateAsync(apiData);

			clearComposerState();

			onClose?.();
		} catch {
			toast.error("Échec lors de l'envoi. Veuillez réessayer.");
		}
	});

	const handleSaveDraft = async () => {
		const formValues = watch();
		const attachmentsPayload = attachments
			.filter(
				(att): att is LocalAttachment & { url: string; contentType: string } =>
					!att.isUploading &&
					att.url !== undefined &&
					att.contentType !== undefined,
			)
			.map((att) => {
				const basePayload = {
					fileName: att.name,
					contentType: att.contentType,
					size: att.size,
					url: att.url,
					source: att.source,
				};
				return mapAttachmentToPayload
					? { ...basePayload, ...mapAttachmentToPayload(att) }
					: basePayload;
			});

		const draftData = {
			to: formatRecipientsForApi(formValues.to),
			cc:
				formValues.cc.length > 0
					? formatRecipientsForApi(formValues.cc)
					: undefined,
			bcc:
				formValues.bcc.length > 0
					? formatRecipientsForApi(formValues.bcc)
					: undefined,
			subject: formValues.subject,
			html: formValues.content,
			attachments:
				attachmentsPayload.length > 0 ? attachmentsPayload : undefined,
			entityType,
			entityId: Number(entityId),
			draftId: draftId ? Number(draftId) : undefined,
		};

		try {
			if (draftId) {
				await updateDraftAsync({ ...draftData, draftId: Number(draftId) });
			} else {
				await saveDraftAsync(draftData);
			}
			setComposerDirty(false);
			setComposerShouldClose(false);
			setShowSaveDraftPrompt(false);

			if (onClose) onClose();
		} catch (error) {
			console.error("Error saving draft:", error);
			toast.error(
				"Erreur lors de la sauvegarde du brouillon. Veuillez réessayer.",
			);
		}
	};

	const handleDiscardDraft = () => {
		setComposerDirty(false);
		setComposerShouldClose(false);
		setShowSaveDraftPrompt(false);

		if (onClose) onClose();
	};

	const getHandleEmailSearch = (
		field: "to" | "cc" | "bcc",
	): ((q: string) => Promise<MultiSelectOption[]>) => {
		switch (field) {
			case "to":
				return toSearch.search;
			case "cc":
				return ccSearch.search;
			case "bcc":
				return bccSearch.search;
			default:
				return async () => [];
		}
	};

	return {
		// Form
		control,
		handleSubmit,
		watch,
		reset,
		isDirty,

		// State
		attachments,
		setAttachments,
		showSaveDraftPrompt,
		setShowSaveDraftPrompt,
		showCcBcc,
		setShowCcBcc,
		showTemplateDialog,
		setShowTemplateDialog,
		isSignatureDialogOpen,
		setIsSignatureDialogOpen,

		// Search
		toSearch,
		ccSearch,
		bccSearch,

		// Templates
		templates: (templates || []) as EmailTemplate[],
		isLoadingTemplates,
		handleTemplateSelection,

		// Handlers
		handleAttachmentAdd,
		handleAttachmentRemove,
		handleExistingDocumentsAdd,
		handleSendEmail,
		handleSaveDraft,
		handleDiscardDraft,
		getHandleEmailSearch,

		// Mutations
		sendEmailMutation,
	};
};
